import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ReactDOM from "react-dom/client";

async function generatePdfFromComponent(Component, props = {}, options = {}) {
  const { pageFormat = "a4", orientation = "p", scale = 2 } = options;

  const tempEl = document.createElement("div");
  Object.assign(tempEl.style, {
    position: "fixed",
    top: "-9999px",
    background: "#fff",
  });
  document.body.appendChild(tempEl);

  const root = ReactDOM.createRoot(tempEl);
  root.render(<Component {...props} />);

  await new Promise((resolve) => setTimeout(resolve, 400));

  const canvas = await html2canvas(tempEl, { scale });
  const imgData = canvas.toDataURL("image/png", 1);

  const pdf = new jsPDF(orientation, "mm", pageFormat);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

  let heightLeft = pdfHeight;
  let position = 0;

  pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
  heightLeft -= pdf.internal.pageSize.getHeight();

  while (heightLeft > 0) {
    position = heightLeft - pdfHeight;
    pdf.addPage();
    pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
    heightLeft -= pdf.internal.pageSize.getHeight();
  }

  root.unmount();
  document.body.removeChild(tempEl);

  return pdf.output("blob");
}

const getValue = (obj, path) =>
  path.split(".").reduce((acc, part) => acc?.[part], obj);

export async function exportToZip(
  items,
  components,
  fileFields = [],
  zipName = "documents",
  onProgress
) {
  const zip = new JSZip();
  const totalCandidates = items.length;

  const mimeToExt = {
    "application/pdf": "pdf",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      "docx",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "text/csv": "csv",
    "text/plain": "txt",
    "application/json": "json",
    "application/zip": "zip",
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/webp": "webp",
    "image/gif": "gif",
  };

  for (let i = 0; i < totalCandidates; i++) {
    const item = items[i];
    const folderName = `${item.firstName || ""}_${item.lastName || "Candidate"}`
      .trim()
      .replace(/\s+/g, "_");
    const folder = zip.folder(folderName);

    for (let j = 0; j < components.length; j++) {
      const { component: Component, fileName } = components[j];
      const pdfBlob = await generatePdfFromComponent(Component, {
        object: item,
      });
      folder.file(`${fileName}.pdf`, pdfBlob);
    }

    for (const field of fileFields) {
      const url = getValue(item, field);
      console.warn(field, url);
      if (url && typeof url === "string" && url.trim() !== "") {
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`Failed to fetch file: ${url}`);
          const blob = await res.blob();

          const contentType = res.headers.get("content-type");
          let ext = mimeToExt[contentType] || "";
          if (!ext) {
            const match = url.match(/\.([a-zA-Z0-9]+)(\?|#|$)/);
            ext = match ? match[1].toLowerCase() : "bin";
          }

          const safeField = field.replace(/\./g, "_");
          const fileName = `${safeField}.${ext}`;
          folder.file(fileName, blob);
        } catch (err) {
          console.warn(`⚠️ Failed to fetch ${field} from ${url}`, err);
          folder.file(
            `${field.replace(/\./g, "_")}_ERROR.txt`,
            `Failed to download: ${url}\n${err}`
          );
        }
      }
    }

    if (typeof onProgress === "function") {
      const percent = Math.round(((i + 1) / totalCandidates) * 100);
      onProgress(percent, i + 1, totalCandidates);
    }
  }

  const content = await zip.generateAsync(
    { type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } },
    (metadata) => {
      if (typeof onProgress === "function") {
        onProgress(
          Math.round(metadata.percent),
          totalCandidates,
          totalCandidates
        );
      }
    }
  );

  saveAs(content, `${zipName}.zip`);
}
